import StencilHTMLElement from "./stencil-element";

class GoogleMap extends StencilHTMLElement {
  static loading = false;

  name = "GoogleMap";
  apiKey;

  constructor() {
    super();
  }

  connectedCallback() {
    this.apiKey = this.getAttribute('api-key');
    if (!this.apiKey) {
      console.error(this.logPrefix, "Google Maps API key is required.");
    }

    // Check if Google Maps is already loaded: if so, render the map. Otherwise, load the API.
    if(typeof window.google === 'object' && typeof window.google.maps === 'object') {
      this.renderMap();
    } else if(!this.constructor.loading) {
      this.loadGoogleMaps();
    }
    
    document.addEventListener("google-maps:loaded", this.renderMap.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener("google-maps:loaded", this.renderMap.bind(this));
  }

  loadGoogleMaps() {
    this.constructor.loading = true;

    // Inject the Google Maps API script into the head
    let script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=onGoogleMapsLoaded`;
    script.async = true;
    document.head.appendChild(script);

    // Emit event and reset static loading flag when the script is loaded
    window.onGoogleMapsLoaded = () => {
      this.constructor.loading = false;
      document.dispatchEvent(new CustomEvent("google-maps:loaded"));
      delete window.onGoogleMapsLoaded;
    };
  }

  renderMap() {
    console.log(this.logPrefix, "Rendering map...");
    let mapStyle = this.querySelector("[data-map-style]")?.textContent;
    if (mapStyle) {
      mapStyle = JSON.parse(mapStyle); 
    }

    const mapMarkersJson = this.querySelector("[data-map-markers]")?.textContent;
    const markers = mapMarkersJson ? JSON.parse(mapMarkersJson) : [];

    // Show marker?
    const showMarker = this.getAttribute('show-marker') === 'true';

    // Default zoom
    const defaultZoom = parseFloat(this.getAttribute('default-zoom'));

    // Default center
    const defaultLat = parseFloat(this.getAttribute('latitude'));
    const defaultLng = parseFloat(this.getAttribute('longitude'));

    const map = new google.maps.Map(this.querySelector(".map"), {
      zoom: defaultZoom,
      center: { lat: defaultLat, lng: defaultLng },
      styles: mapStyle && typeof mapStyle === 'object' ? mapStyle : null
    });
    
    if (markers.length > 0) {
      const bounds = new google.maps.LatLngBounds();

      markers.forEach(markerData => {
        const latitude = parseFloat(markerData.latitude);
        const longitude = parseFloat(markerData.longitude);
        const content = atob(markerData.content);
        const markerIcon = markerData.icon.url;
        const markerWidth = markerData.icon.width;
        const markerHeight = markerData.icon.height;

        // Check if we have a usable icon
        const hasValidIcon = markerIcon && markerWidth > 0 && markerHeight > 0;

        const markerOptions = {
          position: { lat: latitude, lng: longitude },
          map,
        };

        if (hasValidIcon) {
          markerOptions.icon = {
            url: markerIcon,
            scaledSize: new google.maps.Size(markerWidth, markerHeight),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(markerWidth / 2, markerHeight), // Center the anchor nicely
          };
        }
        // Else: no icon field => will use default Google Maps red marker

        const marker = new google.maps.Marker(markerOptions);
  
        const infowindow = new google.maps.InfoWindow({
          content: content,
          maxWidth: 260
        });
  
        // Open infowindow on marker click
        marker.addListener('click', () => {
          infowindow.open(map, marker);
        });
        
        bounds.extend(marker.getPosition());
      });

      map.fitBounds(bounds, 30);
      // Set max zoom level
      map.maxDefaultZoom = defaultZoom;
      google.maps.event.addListenerOnce(map, "bounds_changed", function () {
        this.setZoom(Math.min(this.getZoom(), this.maxDefaultZoom));
      });

    } else if (showMarker) {
      const defaultMarker = new google.maps.Marker({
        position: { lat: defaultLat, lng: defaultLng },
        zoom: defaultZoom,
        map
      });
    }
  }
}

export default GoogleMap;